import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CreditCard, Landmark, CheckCircle2, Copy, MessageCircle, Download, Calendar } from "lucide-react";
import { api, API_BASE as API } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { SITE, waLink } from "@/lib/site";

const fmtBooking = (d, t) => {
  if (!d) return "";
  try {
    const dt = new Date(d + "T00:00:00");
    const s = dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    return t ? `${s} at ${t}` : s;
  } catch { return d; }
};

const statusLabel = (s, isInvoice) =>
  ({ sent: isInvoice ? "Awaiting payment" : "Awaiting advance payment",
     accepted: "Quote accepted — awaiting advance",
     advance_paid: "Advance received — awaiting service",
     service_completed: "Invoice ready — pay final balance",
     final_paid: "Paid in full — thank you",
     cancelled: "Cancelled" }[s] || s);

export const PublicQuote = () => {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("card");
  const [payBusy, setPayBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/public/quotes/${token}`); setQuote(data); }
    catch { toast.error("Quote not found"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  // Handle Stripe return: poll status
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    const poll = async (attempt = 0) => {
      if (cancelled || attempt >= 10) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment confirmed — thank you!");
          setParams({}, { replace: true });
          load();
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params]); // eslint-disable-line

  const pay = async () => {
    setPayBusy(true);
    try {
      const { data } = await api.post(`/public/quotes/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPayBusy(false);
    }
  };

  const copy = (t, l) => navigator.clipboard.writeText(t).then(() => toast.success(l));

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!quote) return <div className="min-h-screen flex items-center justify-center text-slate-500">Quote not found.</div>;

  const remaining = quote.total_pounds - quote.advance_pounds;
  const paymentDue =
    (quote.status === "sent" || quote.status === "accepted") && quote.advance_pounds > 0 ? { stage: quote.is_invoice ? "Amount due" : "Advance to secure booking", amount: quote.advance_pounds } :
    (quote.status === "advance_paid" || quote.status === "service_completed") && remaining > 0 ? { stage: "Remaining balance", amount: remaining } :
    null;

  return (
    <main data-testid="public-quote" className="min-h-screen bg-[#F8FAFC]">
      <Seo title={quote.is_invoice ? `Invoice ${quote.invoice_number || ""}` : "Your BoxB Solutions quote"} description="Secure your booking online" canonical={`/quote/${token}`} noindex />
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-10 w-10 rounded-2xl bg-[#0A192F] text-white font-display font-extrabold items-center justify-center text-xl">B</span>
            <span className="font-display font-bold text-[#0A192F] text-lg tracking-tight">BoxB<span className="text-[#2563EB]">.</span></span>
          </div>
          {quote.is_invoice && (
            <a href={`${API}/public/quotes/${token}/invoice.pdf`} target="_blank" rel="noopener noreferrer"
               data-testid="pdf-download-btn"
               className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 hover:border-[#0A192F] text-[#0A192F] px-4 py-2 text-sm font-semibold">
              <Download className="h-4 w-4" /> PDF
            </a>
          )}
        </div>

        <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#0A192F] tracking-tight">
          {quote.is_invoice ? `Invoice ${quote.invoice_number || ""}` : `Your quote is ready, ${quote.customer_name}.`}
        </h1>
        <p className="mt-2 text-slate-600">{statusLabel(quote.status, quote.is_invoice)}</p>
        {quote.is_invoice && quote.customer_address && (
          <div data-testid="invoice-address" className="mt-4 rounded-2xl bg-white border border-slate-200 p-4 text-sm text-[#0A192F]">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Bill to</div>
            <div className="mt-1 font-semibold">{quote.customer_name}</div>
            <div className="text-slate-600 whitespace-pre-line">{quote.customer_address}</div>
          </div>
        )}

        {quote.booking_date && (
          <div data-testid="booking-info" className="mt-4 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#152742] text-white p-5 flex items-center gap-4">
            <span className="inline-flex h-11 w-11 rounded-2xl bg-white/10 items-center justify-center"><Calendar className="h-5 w-5" /></span>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-white/60">Your booking</div>
              <div className="mt-0.5 font-display font-semibold text-lg sm:text-xl">{fmtBooking(quote.booking_date, quote.booking_time)}</div>
            </div>
          </div>
        )}

        <div className="mt-8 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
          <div className="grid sm:grid-cols-3 gap-4">
            <SummaryRow label="Total" value={`£${quote.total_pounds.toFixed(2)}`} big />
            <SummaryRow label="Advance" value={`£${quote.advance_pounds.toFixed(2)}`} />
            <SummaryRow label="Remaining" value={`£${remaining.toFixed(2)}`} />
          </div>

          {quote.quote_type === "hourly" && quote.base_rate > 0 && (
            <div data-testid="rate-card" className="mt-6 rounded-2xl bg-[#0A192F] text-white p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-white/60">Rate card</div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <div>
                  <span className="font-display text-3xl font-bold">£{quote.base_rate.toFixed(2)}</span>
                  <span className="ml-1 text-sm text-white/70">/hr × {quote.base_hours} hrs = £{(quote.base_rate * quote.base_hours).toFixed(2)} base</span>
                </div>
                <div>
                  <span className="font-display text-3xl font-bold">£{quote.extra_hour_rate.toFixed(2)}</span>
                  <span className="ml-1 text-sm text-white/70">/hr thereafter</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-white/70">
                {quote.service_type ? `${quote.service_type} · ` : ""}
                Pay <b>£{quote.advance_pounds.toFixed(2)}</b> to secure your booking. Any additional hours are billed after the job is complete.
              </p>
              {quote.actual_hours != null && (
                <p className="mt-2 text-sm text-white/90">
                  Actual hours worked: <b>{quote.actual_hours} hrs</b>
                  {quote.actual_hours > quote.base_hours && (
                    <> — extra {(quote.actual_hours - quote.base_hours).toFixed(2)} hrs × £{quote.extra_hour_rate.toFixed(2)}/hr = <b>£{((quote.actual_hours - quote.base_hours) * quote.extra_hour_rate).toFixed(2)}</b> extra</>
                  )}
                </p>
              )}
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Service</div>
            <p className="mt-1 text-[#0A192F] whitespace-pre-line">{quote.service_description}</p>
          </div>
        </div>

        {paymentDue ? (
          <div className="mt-8 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
            <h2 className="font-display font-semibold text-2xl text-[#0A192F]">
              Pay £{paymentDue.amount.toFixed(2)}
            </h2>
            <p className="mt-1 text-slate-600 text-sm">{paymentDue.stage}</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <MethodTab active={method === "card"} onClick={() => setMethod("card")} icon={CreditCard} title="Card payment" sub="Secure Stripe checkout" tid="tab-card" />
              <MethodTab active={method === "bank"} onClick={() => setMethod("bank")} icon={Landmark} title="Bank transfer" sub={quote.bank?.name} tid="tab-bank" />
            </div>

            {method === "card" ? (
              <div className="mt-6">
                <button onClick={pay} disabled={payBusy} data-testid="stripe-pay-btn"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#0A192F] text-white px-8 py-4 font-semibold hover:bg-[#0F172A] disabled:opacity-60">
                  {payBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to Stripe…</> : <><CreditCard className="h-4 w-4" /> Pay £{paymentDue.amount.toFixed(2)} by card</>}
                </button>
                <p className="mt-3 text-xs text-slate-500">You'll be redirected to Stripe. Test card: 4242 4242 4242 4242.</p>
              </div>
            ) : (
              <div data-testid="bank-details" className="mt-6 rounded-2xl bg-[#F8FAFC] border border-slate-200 p-5">
                <div className="grid sm:grid-cols-3 gap-4 text-sm">
                  <BankRow label="Account name" value={quote.bank?.name} onCopy={() => copy(quote.bank?.name, "Copied")} />
                  <BankRow label="Sort code" value={quote.bank?.sort_code} onCopy={() => copy(quote.bank?.sort_code, "Copied")} />
                  <BankRow label="Account number" value={quote.bank?.account} onCopy={() => copy(quote.bank?.account, "Copied")} />
                </div>
                <div className="mt-4 text-sm">
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Reference</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="rounded bg-white border border-slate-200 px-2 py-1 text-[#0A192F]">BOXB-{token.slice(0,8).toUpperCase()}</code>
                    <button onClick={() => copy(`BOXB-${token.slice(0,8).toUpperCase()}`, "Copied")} className="text-xs text-[#2563EB] hover:underline">Copy</button>
                  </div>
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  Please quote the reference on your transfer. Once received, we'll mark your booking confirmed and message you. Any questions?{" "}
                  <a href={waLink(`Hi BoxB, I'm about to send a bank transfer for quote ${token.slice(0,8).toUpperCase()}.`)} target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-semibold">WhatsApp us</a>.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div data-testid="payment-confirmed" className="mt-8 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 text-center">
            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            <h2 className="mt-3 font-display font-semibold text-2xl text-[#0A192F]">
              Thank you — payment received.
            </h2>
            {quote.booking_date ? (
              <p className="mt-2 text-slate-700">
                Your booking is confirmed for <b className="text-[#0A192F]">{fmtBooking(quote.booking_date, quote.booking_time)}</b>.
              </p>
            ) : (
              <p className="mt-2 text-slate-700">We'll be in touch shortly to confirm the date & time.</p>
            )}
            <p className="mt-2 text-slate-500 text-sm">
              {quote.status === "final_paid"
                ? "Paid in full. A receipt is on its way to your email."
                : "We've received your advance — see you on the day!"}
            </p>
          </div>
        )}

        <div className="mt-8 flex items-center justify-center gap-3 text-sm">
          <a href={`tel:${SITE.phoneRaw}`} className="text-slate-500 hover:text-[#0A192F]">{SITE.phone}</a>
          <span className="text-slate-300">·</span>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-semibold inline-flex items-center gap-1">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
        </div>
      </div>
    </main>
  );
};

const SummaryRow = ({ label, value, big }) => (
  <div>
    <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
    <div className={`mt-1 font-display font-semibold text-[#0A192F] ${big ? "text-3xl" : "text-xl"}`}>{value}</div>
  </div>
);

const MethodTab = ({ active, onClick, icon: Icon, title, sub, tid }) => (
  <button type="button" onClick={onClick} data-testid={tid}
          className={`text-left rounded-2xl border p-4 flex items-center gap-3 transition ${active ? "border-[#0A192F] bg-[#0A192F] text-white" : "border-slate-200 bg-white text-[#0A192F] hover:border-[#0A192F]"}`}>
    <Icon className="h-5 w-5" />
    <div>
      <div className="font-semibold">{title}</div>
      <div className={`text-xs ${active ? "text-white/70" : "text-slate-500"}`}>{sub}</div>
    </div>
  </button>
);

const BankRow = ({ label, value, onCopy }) => (
  <div>
    <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
    <div className="mt-1 flex items-center gap-2">
      <span className="text-[#0A192F] font-semibold">{value}</span>
      <button onClick={onCopy} className="text-[#2563EB] hover:text-[#0A192F]"><Copy className="h-3.5 w-3.5" /></button>
    </div>
  </div>
);
